import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";

export type ProjectCommentsToolsTab = "comments" | "find";

export type ProjectCommentsBadge = {
  /**
   * The viewer's unread threads, once their read times load. `"loading"` before the first load,
   * `"unavailable"` when that load failed: either way no count is known.
   */
  unreadThreadCount: number | "loading" | "unavailable";
  unresolvedOpenThreadCount: number;
};

export type ProjectCommentsLicenseLockedAction = {
  onOpenLicensePurchase: () => void;
} | null;

export type ProjectCommentsChromeApi = {
  openToolsSheet: (tab: ProjectCommentsToolsTab) => void;
  commentsBadge: ProjectCommentsBadge;
  licenseLockedAction: ProjectCommentsLicenseLockedAction;
};

const ProjectCommentsChromeContext = createContext<ProjectCommentsChromeApi | null>(null);

const ProjectCommentsChromeRegisterContext = createContext<Dispatch<
  SetStateAction<ProjectCommentsChromeApi | null>
> | null>(null);

const ProjectCommentsLicenseRegisterContext = createContext<Dispatch<
  SetStateAction<ProjectCommentsLicenseLockedAction>
> | null>(null);

export function ProjectCommentsChromeHost({ children }: { children: ReactNode }) {
  const [chrome, setChrome] = useState<ProjectCommentsChromeApi | null>(null);
  const [licenseLockedAction, setLicenseLockedAction] =
    useState<ProjectCommentsLicenseLockedAction>(null);

  const mergedChrome =
    chrome != null
      ? {
          ...chrome,
          licenseLockedAction,
        }
      : null;

  return (
    <ProjectCommentsChromeRegisterContext.Provider value={setChrome}>
      <ProjectCommentsLicenseRegisterContext.Provider value={setLicenseLockedAction}>
        <ProjectCommentsChromeContext.Provider value={mergedChrome}>
          {children}
        </ProjectCommentsChromeContext.Provider>
      </ProjectCommentsLicenseRegisterContext.Provider>
    </ProjectCommentsChromeRegisterContext.Provider>
  );
}

export function useProjectCommentsChrome(): ProjectCommentsChromeApi | null {
  return useContext(ProjectCommentsChromeContext);
}

/**
 * Registers header comment actions and badge counts for the navbar Comments button.
 * Clears chrome on unmount only (badge updates do not reset chrome).
 */
export function useRegisterProjectCommentsChrome(
  openToolsSheet: ProjectCommentsChromeApi["openToolsSheet"],
  commentsBadge: ProjectCommentsBadge
) {
  const setChrome = useContext(ProjectCommentsChromeRegisterContext);
  const openToolsSheetRef = useRef(openToolsSheet);
  openToolsSheetRef.current = openToolsSheet;

  useEffect(() => {
    if (!setChrome) return;
    setChrome({
      openToolsSheet: (tab) => openToolsSheetRef.current(tab),
      commentsBadge,
      licenseLockedAction: null,
    });
  }, [setChrome, commentsBadge.unreadThreadCount, commentsBadge.unresolvedOpenThreadCount]);

  useEffect(() => {
    return () => {
      setChrome?.(null);
    };
  }, [setChrome]);
}

/** Script editor registers upgrade CTA without replacing project-level comments chrome. */
export function useRegisterProjectCommentsLicenseAction(
  licenseLockedAction: ProjectCommentsLicenseLockedAction
) {
  const setLicense = useContext(ProjectCommentsLicenseRegisterContext);
  const onOpenLicensePurchaseRef = useRef(licenseLockedAction?.onOpenLicensePurchase);
  onOpenLicensePurchaseRef.current = licenseLockedAction?.onOpenLicensePurchase;

  const licenseLocked = licenseLockedAction != null;

  useEffect(() => {
    if (!setLicense) return;
    setLicense(
      licenseLocked
        ? {
            onOpenLicensePurchase: () => onOpenLicensePurchaseRef.current?.(),
          }
        : null
    );
  }, [setLicense, licenseLocked]);

  useEffect(() => {
    return () => {
      setLicense?.(null);
    };
  }, [setLicense]);
}
